<template>
  <g v-if="showResponseOption" class="drag-drop-response-option" :class="classes">
    <foreignObject>
      <div v-if="showOptionLabel" class="option-label">
        <span>{{ responseOption.optionLabel }}</span>
      </div>
      <img v-if="isImageOption" :src="imageSrc" />
      <div v-else ref="textOption" class="text-option">
        {{ responseOption.textValue }}
      </div>
    </foreignObject>
    <g v-if="showIcon" ref="icon" class="icon">
      <svg-icon v-if="showCorrectIcon" name="CheckmarkSolid16" kind="indicative" />
      <svg-icon v-else-if="showWrongIcon" name="Misuse16" kind="indicative" />
    </g>
  </g>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, useTemplateRef, watch } from "vue";
import appConfigs from "@configs/app";
import DOMUtils from "@utils/dom.utils";
import { useVm } from "@/utils/vm";

interface ResponseOption {
  xCoordinate: number | string;
  yCoordinate: number | string;
  height: number;
  width: number;
  isCorrect: boolean;
  fileName: string;
  textValue: string;
  dropTargetId: number;
  optionLabel?: string;
  [key: string]: unknown;
}

interface Props {
  responseOption?: ResponseOption;
  questionUuid?: string;
  bgImageRatio?: number;
  showOptionLabel?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  responseOption: () => ({
    xCoordinate: "",
    yCoordinate: "",
    height: 0,
    width: 0,
    isCorrect: false,
    fileName: "",
    textValue: "",
    dropTargetId: 0,
  }),
  questionUuid: "",
  bgImageRatio: 1,
  showOptionLabel: false,
});

const { vm } = useVm();

const textOptionRef = useTemplateRef("textOption");
const iconRef = useTemplateRef("icon");

const imageSrc = computed(
  () =>
    `${appConfigs.s3ResourceDomain}/question/${props.questionUuid}/${props.responseOption.fileName}`,
);
const isImageOption = computed(() => !!props.responseOption.fileName);
const classes = computed(() => ({
  correct: props.responseOption.isCorrect,
  wrong: !props.responseOption.isCorrect,
}));
const showResponseOption = computed(() => props.responseOption.dropTargetId !== 0);
const showCorrectIcon = computed(() => props.responseOption.isCorrect);
const showWrongIcon = computed(() => !props.responseOption.isCorrect);
const showOptionLabel = computed(() => props.showOptionLabel);
const showIcon = computed(() => !showOptionLabel.value);

const setIconDimension = () => {
  const iconSvg = DOMUtils.elOf(iconRef.value as Element, "svg")[0];
  if (!iconSvg) return;
  const widthAttr = iconSvg.getAttribute("width");
  const heightAttr = iconSvg.getAttribute("height");
  const currentWidth = parseInt(widthAttr ?? "0");
  const currentHeight = parseInt(heightAttr ?? "0");
  const rect = iconSvg.getBoundingClientRect();
  const scale = rect.width ? currentWidth / rect.width : 1;
  const newWidth = Math.max(16, currentWidth * props.bgImageRatio * scale);
  const newHeight = Math.max(16, currentHeight * props.bgImageRatio * scale);
  iconSvg.setAttribute("width", String(newWidth));
  iconSvg.setAttribute("height", String(newHeight));
};

const setDimensions = () => {
  setIconDimension();
  const d3 = vm.$d3;
  const rootEl = vm.$el;
  if (!d3 || !rootEl) return;

  const d3ResponseOption = d3.select(rootEl);
  const width = props.responseOption.width * props.bgImageRatio;
  const height = props.responseOption.height * props.bgImageRatio;
  const xCoordinate = Number(props.responseOption.xCoordinate) * props.bgImageRatio;
  const yCoordinate = Number(props.responseOption.yCoordinate) * props.bgImageRatio;
  const foreignObject = d3ResponseOption.select("foreignObject");

  foreignObject.attr("width", width).attr("height", height);
  d3ResponseOption.node().style.transform = `translate(${xCoordinate}px, ${yCoordinate}px)`;

  if (!props.responseOption.fileName) {
    const textDiv = foreignObject.select("div .text-option");
    const currentFontSize = window
      .getComputedStyle(foreignObject.node(), null)
      .fontSize.replace(/px/, "");
    const newFontSize = Number(currentFontSize) * props.bgImageRatio;

    const currentPadding = window
      .getComputedStyle(textOptionRef.value as Element, null)
      .padding.replace(/px/, "");
    const newPadding = Number(currentPadding) * props.bgImageRatio;

    textDiv.node().style.fontSize = `${newFontSize}px`;
    textDiv.node().style.padding = `${newPadding}px`;
  }

  if (showOptionLabel.value) {
    const optionLabelDiv = foreignObject.select("div .option-label");
    const currentWidth = window
      .getComputedStyle(foreignObject.node(), null)
      .width.replace(/px/, "");
    const currentHeight = window
      .getComputedStyle(foreignObject.node(), null)
      .height.replace(/px/, "");
    optionLabelDiv.node().style.width = `${Number(currentWidth) * props.bgImageRatio}px`;
    optionLabelDiv.node().style.height = `${Number(currentHeight) * props.bgImageRatio}px`;
  }
};

watch(
  () => props.bgImageRatio,
  () => setDimensions(),
);

onMounted(() => {
  nextTick(() => {
    setDimensions();
  });
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";

.drag-drop-response-option {
  &.correct {
    foreignObject {
      outline: 1px solid $drag-drop-correct;
    }
    > .icon {
      [fill] {
        fill: $drag-drop-correct;
      }
    }
  }
  &.wrong {
    foreignObject {
      outline: 1px solid $drag-drop-wrong;
    }
    > .icon {
      [fill] {
        fill: $drag-drop-wrong;
      }
    }
  }

  foreignObject {
    div {
      display: flex;
      height: 100%;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: $spacing-05;
      background-color: $ui-01;
      word-break: break-word;
      &.text-option {
        line-height: normal;
      }
    }
    img {
      width: 100%;
      height: 100%;
      background-color: $ui-01;
      object-fit: cover;
    }
  }

  .option-label {
    position: absolute;
    top: 0.25rem;
    left: 0.25rem;
    max-height: 1.5rem;
    min-height: 1.5rem;
    max-width: 1.5rem;
    padding: $spacing-03;
    background-color: $ui-01;
    color: $state-positive;
    border: 1px solid $state-positive;
    border-radius: 5px;
    > span {
      position: absolute;
      width: 100%;
      top: 0;
      left: 0;
    }
  }
}
</style>
